import { FC, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CampaignOutlined,
  ReplayOutlined,
  SubjectOutlined,
  VisibilityOffOutlined,
} from '@mui/icons-material';
import { api } from '../../helpers/apiUtils';
import Sprechtext from './Sprechtext';

/**
 * Automatische Karte „Nächstes Webinar" (Plan 2026-11-04). Steht oben in den Einblendungen und muss nicht angelegt
 * werden: Anmeldung zum nächsten Webinar, sonst die Wiederholung, sonst die Aufzeichnung im Fenster. Der Text entsteht
 * auf dem Server beim Abruf und beim Klick, ein festes „Heute" kann hier also nicht veralten.
 *
 * Eigener Abruf statt im Bibliotheks-GET: hängt die Abfrage, bleibt die Liste darunter bedienbar.
 */

export interface AutoKarteDaten {
  kind: 'anmeldung' | 'wiederholung' | 'aufzeichnung';
  event_id: string;
  source_webinar_id: string;
  title: string;
  host: string | null;
  start_ms: number | null;
  headline: string;
  button_label: string;
  button_url: string;
  duration_sec: number;
  cta_id: number | null;
  clicks_today: number;
  last_shown_ms: number | null;
}

type Lage =
  | { state: 'card'; card: AutoKarteDaten }
  | { state: 'none' }
  | { state: 'error'; details?: string };

const NACHLADEN_MS = 30_000;
const ART_TEXT: Record<AutoKarteDaten['kind'], string> = {
  anmeldung: 'Nächstes Webinar',
  wiederholung: 'Wiederholung',
  aufzeichnung: 'Aufzeichnung',
};

function fehlerText(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { details?: unknown; error?: string } };
    message?: string;
  };
  const d = e?.response?.data;
  if (typeof d?.details === 'string' && d.details) return d.details;
  return d?.error || e?.message || fallback;
}

const AutoKarte: FC<{
  sendungLaeuft: boolean;
  /** Läuft gerade DIESE Karte? Der Elternteil kennt den aktiven Lauf. */
  aktiveCtaId: number | null;
  restText: string | null;
  dauerText: (sek: number) => string;
  zuletztText: (ms: number | null | undefined) => string;
  /** Nach dem Einblenden: Elternteil lädt den aktiven Lauf neu. */
  onGeaendert: () => Promise<void> | void;
  onAusblenden: () => void;
  ausblendenBusy: boolean;
  /** Meldet die Zeile der Karte nach oben: „Neu starten" des Elternteils muss sie erkennen. */
  onKarte: (karte: AutoKarteDaten | null) => void;
}> = ({
  sendungLaeuft,
  aktiveCtaId,
  restText,
  dauerText,
  zuletztText,
  onGeaendert,
  onAusblenden,
  ausblendenBusy,
  onKarte,
}) => {
  const [lage, setLage] = useState<Lage | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Beim Öffnen festgehalten: ein gescheiterter Abruf oder ein Kartenwechsel darf den Text nicht mitten im Vorlesen wegnehmen.
  const [leseKarte, setLeseKarte] = useState<AutoKarteDaten | null>(null);

  const laden = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: Lage }>('/api/live/admin/auto-karte');
      setLage(res.data.data);
    } catch {
      // Ein Netz-Aussetzer nimmt eine sichtbare Karte nicht weg; der Klick fragt ohnehin frisch und meldet einen Fehler.
      setLage((vorher) => (vorher?.state === 'card' ? vorher : { state: 'error' }));
    }
  }, []);

  useEffect(() => {
    laden();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') laden();
    }, NACHLADEN_MS);
    return () => clearInterval(id);
  }, [laden]);

  const karte = lage?.state === 'card' ? lage.card : null;
  useEffect(() => {
    onKarte(karte);
  }, [karte, onKarte]);

  const einblenden = async (k: AutoKarteDaten) => {
    setBusy(true);
    setFehler(null);
    try {
      await api.post('/api/live/admin/auto-karte/show', { kind: k.kind, event_id: k.event_id });
      await Promise.all([laden(), onGeaendert()]);
    } catch (err) {
      setFehler(fehlerText(err, 'Einblenden hat nicht geklappt.'));
      // Etwa `karte_veraltet`: sofort den aktuellen Stand zeigen.
      await laden();
    } finally {
      setBusy(false);
    }
  };

  const istAktiv = (k: AutoKarteDaten) => k.cta_id != null && aktiveCtaId === k.cta_id;
  // In der Leseansicht die frische Zeile derselben Karte: erst nach dem ersten Klick gibt es eine `cta_id`.
  const leseStand =
    leseKarte && karte && karte.kind === leseKarte.kind && karte.event_id === leseKarte.event_id
      ? karte
      : leseKarte;

  let flaeche;
  if (!lage) {
    flaeche = (
      <Box display="flex" justifyContent="center" my={2}>
        <CircularProgress size={22} />
      </Box>
    );
  } else if (lage.state === 'error') {
    flaeche = (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        {lage.details ||
          'Das nächste Webinar lässt sich gerade nicht ermitteln. Einblendungen aus der Liste gehen weiter.'}
      </Alert>
    );
  } else if (lage.state === 'none' || !karte) {
    flaeche = (
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Gerade gibt es nichts automatisch zu bewerben: keine offene Anmeldung, keine Wiederholung
        und keine Aufzeichnung im Fenster. Eigene Einblendungen stehen unten.
      </Alert>
    );
  } else {
    const aktiv = istAktiv(karte);
    flaeche = (
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 1.5, borderColor: aktiv ? '#0A6634' : '#477DEA', borderWidth: 2 }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'flex-start' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Chip
              size="small"
              label={`${ART_TEXT[karte.kind]} · automatisch`}
              sx={{ mb: 0.75, fontWeight: 600, bgcolor: '#E5EDFD', color: '#1B3F8B' }}
            />
            <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              {karte.headline}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ mt: 0.5, overflowWrap: 'anywhere' }}
            >
              Knopf „{karte.button_label}“ → {karte.button_url}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Klicks heute: <strong>{karte.clicks_today}</strong> ·{' '}
              {zuletztText(karte.last_shown_ms)}
            </Typography>
          </Box>
          <Stack spacing={0.75} sx={{ minWidth: { md: 220 } }}>
            {aktiv ? (
              <>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#0A6634', textAlign: { md: 'center' } }}
                >
                  Läuft · noch {restText}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<VisibilityOffOutlined />}
                  disabled={ausblendenBusy}
                  onClick={onAusblenden}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  Ausblenden
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<ReplayOutlined />}
                  disabled={busy || !sendungLaeuft}
                  onClick={() => einblenden(karte)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {busy ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    `Neu starten · ${dauerText(karte.duration_sec)}`
                  )}
                </Button>
              </>
            ) : (
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CampaignOutlined />}
                disabled={!sendungLaeuft || busy}
                onClick={() => einblenden(karte)}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {busy ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `Einblenden · ${dauerText(karte.duration_sec)}`
                )}
              </Button>
            )}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<SubjectOutlined />}
              onClick={() => setLeseKarte(karte)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Sprechtext lesen
            </Button>
          </Stack>
        </Stack>
        {fehler && !leseKarte && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setFehler(null)}>
            {fehler}
          </Alert>
        )}
      </Paper>
    );
  }

  return (
    <>
      {flaeche}
      {leseStand && (
        <Sprechtext
          offen
          karte={{ kind: leseStand.kind, event_id: leseStand.event_id }}
          onClose={() => setLeseKarte(null)}
          onEinblenden={() => einblenden(leseStand)}
          onAusblenden={onAusblenden}
          sendungLaeuft={sendungLaeuft}
          laeuftNoch={istAktiv(leseStand) && restText ? `noch ${restText}` : null}
          busy={busy || ausblendenBusy}
          knopfText={`Einblenden · ${dauerText(leseStand.duration_sec)}`}
          aussenFehler={fehler}
          onAussenFehlerWeg={() => setFehler(null)}
        />
      )}
    </>
  );
};

export default AutoKarte;
